import { ExternalServiceId, InternalId } from '@edgebox/data-definition-kit';
import { Accordion, Badge, Button, ButtonGroup, ButtonLink, Dropdown, Form, LeftRightContainer, Modal } from '@edgebox/react-components';
import { EntityRemoteStatus, SyndicationStatus } from '@edgebox/sync-core-data-definitions';
import { ClientPreviewItem, ClientRemoteEntityCloneEntity, ClientSyndicationEntity } from '@edgebox/sync-core-rest-client';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons/faArrowUpRightFromSquare';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React from 'react';
import { toast } from 'react-toastify';
import { ISyncCoreApiComponentState, SyncCoreApiComponent } from '../../../../../services/SyncCoreApiComponent';
import { EmbeddedModal } from '../../../../EmbeddedModal';
import { PoolSummaryIcon } from '../../../../PoolSummaryIcon';
import { SiteName } from '../../../../SiteName';
import { doneStatus, SyndicationStatusIcon } from '../../../SyndicationStatusIcon';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { modalClose, modalOpen } from '../../../../../frame-messages';
import { faCheckCircle } from '@fortawesome/pro-light-svg-icons/faCheckCircle';
import { faTrashCan } from '@fortawesome/pro-light-svg-icons/faTrashCan';
import { faClock } from '@fortawesome/pro-light-svg-icons/faClock';
import { Nugget } from '../../../../Nugget';
import { getEntityTypeIcon } from '../../../EntityTypeIcon';
import { FormatDate } from '../../../../FormatDate';
import { ExternalLinkWithIcon } from '../../../../ExternalLinkWithIcon';
import { ICON_PULL } from '../../../../../Helpers';
import { EntityStatusWithParams } from '../../EntityStatus/EntityStatus';
import HasMultiplePools from './HasMultiplePools';
import ItemPreview from './ItemPreview';
import Serialize from './Serialize';
import { WithSite } from '../../../../WithSite';
import WithMostRecentSyndications from './WithMostRecentSyndications';
import WithRemoteRevisionEntity from '../../EntityStatus/WithRemoteRevisionEntity';
import { faClone } from '@fortawesome/pro-light-svg-icons/faClone';
import { faArrowsRotate } from '@fortawesome/pro-light-svg-icons/faArrowsRotate';
import { RemoteEntityCloneList } from '../../../../RemoteEntityCloneList';
import { ISiteEntityTypeInfo, SiteEntityName } from '../../../../SiteEntityName';

// Start with refreshing every second.
const REFRESH_INTERVAL_RUNNING = 1_000;
// Double the refresh interval every 4 times.
const REFRESH_INTERVAL_DOUBLE_TIME = 4;
const REFRESH_INTERVAL_ISSUE = 20_000;

interface IItemProps {
  item: ClientPreviewItem;
  pullWithFlowMachineName?: ExternalServiceId;
  manualPullFlowOptions?: Array<{
    machineName: ExternalServiceId;
    name: string;
    clone: boolean | undefined;
  }>;
  manualCloneEnabled?: boolean;
  configurationAccess?: boolean;
  imagePreviewJwt?: string;
  addTextFilter?: (text: string) => void;
  viewMode?: 'list' | 'preview';
  forcePull?: boolean;
}

type SerializedModeType = 'table' | 'yaml-pretty' | 'yaml-raw';

interface IItemState extends ISyncCoreApiComponentState {
  refreshCount?: number;
  item?: ClientPreviewItem;
  syndication?: ClientSyndicationEntity;
  cloneEntity?: ClientRemoteEntityCloneEntity;
  cloneSummary?: {
    total: number;
    items: ClientRemoteEntityCloneEntity[];
  };
  cloneSummaryEntityTypes?: Record<string, ISiteEntityTypeInfo>;
  pullTriggered?: boolean;
  pullingEntity?: boolean;
  serialize?: boolean;
  showUpdate?: boolean;
  serializeMode?: SerializedModeType;
  selectedComparison?: number;
  selectedComparisonId?: InternalId;

  showPullModeModal?: boolean;
  selectedManualFlow?: ExternalServiceId;
  openWhenDone?: boolean;
  openWhenDoneArmed?: boolean;
  autoOpenedForSyndicationId?: InternalId;

  showCloneHistoryModal?: boolean;
}

const OPEN_WHEN_DONE_STORAGE_KEY = 'pd-openWhenDone';

export class PullDashboardItem extends SyncCoreApiComponent<IItemProps, IItemState> {
  private getViewUrl(item: ClientPreviewItem, syndication?: ClientSyndicationEntity): string | undefined {
    const { forcePull } = this.props;
    const { pullingEntity } = this.state;

    const wasDeleted = item.localUsage && item.localUsage.status !== EntityRemoteStatus.Exists;
    const deleted = wasDeleted && (!this.state.syndication || this.state.syndication.id === this.props.item.lastPull?.id);

    if (deleted || pullingEntity) {
      return undefined;
    }

    if (syndication) {
      if (!wasDeleted || syndication.status === SyndicationStatus.Finished) {
        if (!forcePull && syndication.isClone && this.state.cloneEntity?.viewUrl) {
          return this.state.cloneEntity.viewUrl;
        }
        return syndication.usage?.viewUrl || item.localUsage?.viewUrl;
      }
      return undefined;
    }

    return !wasDeleted ? item.localUsage?.viewUrl : undefined;
  }

  componentDidUpdate(prevProps: Readonly<IItemProps>, prevState: Readonly<IItemState>): void {
    const item = this.state.item || this.props.item;
    const syndication = this.state.syndication || item.lastPull;

    const openWhenDone =
      this.state.openWhenDone !== undefined
        ? this.state.openWhenDone
        : (localStorage.getItem(OPEN_WHEN_DONE_STORAGE_KEY) || 'true') === 'true';

    if (
      this.state.openWhenDoneArmed &&
      openWhenDone &&
      syndication?.status === SyndicationStatus.Finished &&
      this.state.autoOpenedForSyndicationId !== syndication.id
    ) {
      const viewUrlToOpen = this.getViewUrl(item, syndication);
      if (!viewUrlToOpen) {
        return;
      }

      const linkId = `pd-auto-open-${item.entity.id}`;
      const el = document.getElementById(linkId) as HTMLAnchorElement | null;

      if (el) {
        el.click();
      }

      this.setState({
        openWhenDoneArmed: false,
        autoOpenedForSyndicationId: syndication.id,
      });
    }
  }

  async load(stateUpdate?: Pick<IItemState, 'pullingEntity' | 'syndication' | 'pullTriggered'>) {
    const state = { ...this.state, ...(stateUpdate || {}) };
    const item = state.item || this.props.item;
    let syndication = state.syndication || item.lastPull;
    let cloneEntity = state.cloneEntity;
    let cloneSummary = state.cloneSummary;
    let cloneSummaryEntityTypes = state.cloneSummaryEntityTypes;
    const status = syndication?.status;
    const { refreshCount } = state;

    // Refresh even when refreshCount is 0 (0 is the first refresh tick).
    if (refreshCount !== undefined && syndication) {
      syndication = await this.api.syndication.syndications.item(syndication.id, true, { includeUsage: true });
    }

    const hasManualOverlap = !!this.props.manualPullFlowOptions?.length;
    const isClonableInManualContext =
      !this.props.forcePull &&
      ((hasManualOverlap && (this.props.manualPullFlowOptions || []).some((o) => !!o.clone)) ||
        (!hasManualOverlap && !!this.props.manualCloneEnabled));

    // Manual clone syndications need the viewUrl from the RemoteEntityClone entity.
    if (!this.props.forcePull && syndication?.isClone && syndication.cloneEntity && doneStatus.includes(syndication.status)) {
      const cloneId = syndication.cloneEntity.getId();
      if (cloneId && (!cloneEntity || cloneEntity.id !== cloneId)) {
        try {
          cloneEntity = await this.api.syndication.remoteEntityClones.item(cloneId, true);
        } catch {
          // Keep existing behavior if the clone lookup fails for any reason.
          cloneEntity = undefined;
        }
      }
    } else {
      cloneEntity = undefined;
    }

    // Only fetch clone summary when cloning is relevant in the manual context.
    if (!cloneSummary && isClonableInManualContext && item.entity.remoteUuid) {
      try {
        const result = await this.api.syndication.remoteEntityClones.list(
          {
            sourceRemoteUuid: item.entity.remoteUuid,
          },
          { page: 0, itemsPerPage: 3 }
        );

        const items = (result.items || []).filter((c) => {
          const deletedAt = (c as any).deletedAt;
          return c.status === EntityRemoteStatus.Exists && !deletedAt;
        });

        if (result.totalNumberOfItems) {
          cloneSummary = {
            total: result.totalNumberOfItems,
            items: items.slice(0, 3),
          };
        }
      } catch {
        // Ignore failures; clone summary is optional.
      }
    }

    // Resolve entity type info for clone summary name rendering.
    if (cloneSummary?.items?.length) {
      try {
        const existing = cloneSummaryEntityTypes || {};
        const missingTypeIds = Array.from(
          new Set(cloneSummary.items.map((c) => c.entityType?.getId?.()).filter((id): id is any => !!id && !existing[String(id)]))
        );

        if (missingTypeIds.length) {
          const results = await Promise.all(
            missingTypeIds.map(async (typeId) => {
              try {
                const entityType = await this.api.syndication.remoteEntityTypes.item(typeId);
                return [
                  String(typeId),
                  { namespaceMachineName: entityType.namespaceMachineName, machineName: entityType.machineName },
                ] as const;
              } catch {
                return null;
              }
            })
          );

          const updated: Record<string, ISiteEntityTypeInfo> = { ...existing };
          for (const entry of results) {
            if (!entry) continue;
            updated[entry[0]] = entry[1];
          }
          cloneSummaryEntityTypes = updated;
        }
      } catch {
        // Optional enhancement; ignore failures.
      }
    }

    let interval: number;
    if (!status || doneStatus.includes(status)) {
      interval = 0;
    } else if (status === SyndicationStatus.Initializing || status === SyndicationStatus.Running) {
      interval = REFRESH_INTERVAL_RUNNING;
      const doubleTimes = Math.floor(((refreshCount ?? 0) as number) / REFRESH_INTERVAL_DOUBLE_TIME);
      interval *= Math.pow(2, doubleTimes);
    } else {
      interval = REFRESH_INTERVAL_ISSUE;
    }

    if (interval > 0) {
      setTimeout(() => {
        if (!this.__isMounted) {
          return;
        }
        this.load();
      }, interval);
    }

    return {
      ...(stateUpdate || {}),
      refreshCount: refreshCount !== undefined ? refreshCount + 1 : 0,
      syndication,
      cloneEntity,
      cloneSummary,
      cloneSummaryEntityTypes,
    };
  }

  render() {
    const item = this.state.item || this.props.item;
    const syndication = this.state.syndication || item.lastPull;
    const { pullWithFlowMachineName, imagePreviewJwt, viewMode, forcePull, manualPullFlowOptions, manualCloneEnabled } = this.props;
    const {
      pullingEntity,
      showUpdate,
      serialize,
      serializeMode,
      selectedComparison,
      selectedComparisonId,
      pullTriggered,
      showPullModeModal,
      selectedManualFlow,
      showCloneHistoryModal,
    } = this.state;

    const wasDeleted = item.localUsage && item.localUsage.status !== EntityRemoteStatus.Exists;
    const deleted = wasDeleted && (!this.state.syndication || this.state.syndication.id === this.props.item.lastPull?.id);
    const sourceDeleted = item.sourceUsage.status !== EntityRemoteStatus.Exists;
    const viewUrl = this.getViewUrl(item, syndication);

    const canPull = !!pullWithFlowMachineName;
    const exists = viewUrl && !deleted;
    const showPull = !syndication || doneStatus.includes(syndication.status);
    const actionDropDown = true;

    const hasManualOverlap = !!manualPullFlowOptions?.length;

    const isClonableInManualContext =
      !forcePull &&
      ((hasManualOverlap && (manualPullFlowOptions || []).some((o) => !!o.clone)) || (!hasManualOverlap && !!manualCloneEnabled));

    const cloneButtonLabel = (manualPullFlowOptions || []).some((o) => !o.clone)
      ? hasManualOverlap
        ? 'Pull…'
        : 'Pull'
      : hasManualOverlap
        ? 'Clone…'
        : 'Clone';

    const openWhenDone =
      this.state.openWhenDone !== undefined
        ? this.state.openWhenDone
        : (localStorage.getItem(OPEN_WHEN_DONE_STORAGE_KEY) || 'true') === 'true';

    const autoOpenLinkId = `pd-auto-open-${item.entity.id}`;

    const viewSource = (secondary: boolean, embedded = true) =>
      item.sourceUsage.status === EntityRemoteStatus.Exists ? (
        <ExternalLinkWithIcon
          to={item.sourceUsage.viewUrl}
          className={
            embedded ? `${secondary ? `p-2 ${viewUrl ? 'text-muted' : ''}` : `px-1`}` : 'text-decoration-none fs-5 d-block px-3 py-2'
          }
        >
          View source
        </ExternalLinkWithIcon>
      ) : embedded ? (
        <Badge bg="warning" className="ms-1">
          Source deleted
        </Badge>
      ) : null;

    let actions: React.ReactNode[] = [];
    const canViewSource =
      (!exists || item.sourceUsage.viewUrl !== item.localUsage?.viewUrl) && item.sourceUsage.status === EntityRemoteStatus.Exists;
    if (canViewSource) {
      // Only add to the drop-down if it's not the primary action.
      if (exists || canPull) {
        actions.push(viewSource(true, !actionDropDown));
      }
    }

    let showUpdatesButton: React.ReactNode = null;
    if (this.props.configurationAccess) {
      actions.push(
        <Button
          variant="link"
          className={`text-muted ${actionDropDown ? 'px-3 py-2 d-block w-100 text-start text-decoration-none fs-5' : 'px-2'}`}
          onClick={() => {
            this.setState({ serialize: true, selectedComparisonId: undefined });
            modalOpen();
          }}
        >
          Serialize
        </Button>
      );
      showUpdatesButton = (
        <Button
          variant="link"
          className={`text-muted ${actionDropDown ? 'px-3 py-2 d-block w-100 text-start text-decoration-none fs-5' : 'px-2'}`}
          onClick={() => this.setState({ showUpdate: true })}
        >
          Show Updates
        </Button>
      );

      // Only add to the drop-down if it's not the primary action
      if (canViewSource || exists || canPull) {
        actions.push(showUpdatesButton);
      }
    }

    const dropdownActions = actions.filter((c) => !!c);

    const menuItems = (
      <Dropdown.Menu style={{ width: '200px', zIndex: 99999 }} className="shadow-sm">
        {dropdownActions.map((c, index) => (
          <Dropdown.Item className="p-0" key={index} as={'div'}>
            {c}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    );

    if (exists) {
      // If the user can clone content with any manual Flow, Clone becomes the primary action even if the content has been cloned or sync'd before.
      // Unless the user just *finished* cloning; then we show "View here" as primary to avoid confusion.
      const showClonePrimary = isClonableInManualContext && (!pullTriggered || !syndication || !doneStatus.includes(syndication.status));

      if (!showClonePrimary) {
        actions = [
          <>
            <Dropdown align="end" as={ButtonGroup} className="rounded bg-white-selectable shadow-sm" onClick={(e) => e.stopPropagation()}>
              <ButtonLink
                to={viewUrl || ''}
                variant="light"
                className="pull-submit ps-3 pe-3 pt-2 pb-2 fs-5 fw-bold bg-none color-dark"
                disabled={!viewUrl}
                target="_blank"
                style={{ width: '172px' }}
              >
                {canPull ? 'View here' : 'View'} <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
              </ButtonLink>

              <Dropdown.Toggle split variant="light" id="dropdown-split-basic" className="border-start bg-none color-dark" />

              {menuItems}
            </Dropdown>
          </>,
        ];
      } else {
        const preferred = (manualPullFlowOptions || []).find((o) => !!o.clone)?.machineName || manualPullFlowOptions?.[0]?.machineName;

        actions = [
          <>
            <Dropdown align="end" as={ButtonGroup} className="rounded bg-primary-selectable shadow-sm" onClick={(e) => e.stopPropagation()}>
              <Button
                variant="primary"
                className="pull-submit px-3 pt-2 pb-2 fs-5 fw-bold bg-none"
                disabled={!!pullingEntity || !showPull}
                style={{ width: '172px' }}
                onClick={(e) => {
                  e.preventDefault();

                  if (hasManualOverlap) {
                    this.setState({
                      showPullModeModal: true,
                      selectedManualFlow: selectedManualFlow || preferred,
                      openWhenDone,
                    });
                    modalOpen();
                    return;
                  }

                  this.setState({ pullingEntity: true, syndication: undefined });
                  (async () => {
                    try {
                      if (!pullWithFlowMachineName) {
                        throw new Error('No Flow configured for this action.');
                      }

                      const syndication = await this.api.syndication.syndications.pull({
                        flowMachineName: pullWithFlowMachineName,
                        entityTypeMachineName: item.entityTypeVersion.machineName,
                        entityTypeNamespaceMachineName: item.entityTypeVersion.namespaceMachineName,
                        manually: true,
                        remoteUuid: item.entity.remoteUuid,
                        remoteUniqueId: item.entity.remoteUniqueId,
                      });

                      await this.load({ pullingEntity: undefined, pullTriggered: true, syndication });
                    } catch (e) {
                      this.setState({ pullingEntity: undefined });
                      if ((e as Error).message) toast((e as Error).message, { type: 'error' });
                    }
                  })();
                }}
              >
                {pullingEntity && !syndication ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : syndication && !doneStatus.includes(syndication.status) ? (
                  <SyndicationStatusIcon
                    status={syndication.status}
                    errorType={syndication.operations?.[0]?.errors?.[0]?.type}
                    size="sm"
                    background="primary"
                  />
                ) : (
                  <FontAwesomeIcon icon={faClone} className="me-2" />
                )}{' '}
                {cloneButtonLabel}
              </Button>

              <Dropdown.Toggle split variant="primary" id="dropdown-split-basic" className="border-start bg-none" />

              <Dropdown.Menu style={{ width: '200px', zIndex: 99999 }} className="shadow-sm">
                <Dropdown.Item className="p-0" as={'div'}>
                  <ButtonLink
                    to={viewUrl || ''}
                    variant="link"
                    className={`text-muted ${actionDropDown ? 'px-3 py-2 d-block w-100 text-start text-decoration-none fs-5' : 'px-2'}`}
                    disabled={!viewUrl}
                    target="_blank"
                  >
                    View here <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                  </ButtonLink>
                </Dropdown.Item>
                {dropdownActions.map((c, index) => (
                  <Dropdown.Item className="p-0" key={index} as={'div'}>
                    {c}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </>,
        ];
      }
    } else if (canPull) {
      const variant = forcePull ? 'light' : 'primary';
      actions = [
        <>
          <Dropdown
            align="end"
            as={ButtonGroup}
            className={`rounded bg-${variant === 'primary' ? 'primary' : 'white'}-selectable shadow-sm`}
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              variant={variant}
              className="pull-submit px-3 pt-2 pb-2 fs-5 fw-bold bg-none"
              disabled={!!pullingEntity || !showPull}
              style={{ width: '172px' }}
              onClick={(e) => {
                e.preventDefault();

                // Only show the new modal if we have multiple MANUAL overlapping flows.
                if (!forcePull && hasManualOverlap) {
                  const first = manualPullFlowOptions?.[0]?.machineName;
                  this.setState({
                    showPullModeModal: true,
                    selectedManualFlow: selectedManualFlow || first,
                    openWhenDone,
                  });
                  modalOpen();
                  return false;
                }

                this.setState({ pullingEntity: true, syndication: undefined });

                (async () => {
                  try {
                    const syndication = await this.api.syndication.syndications.pull({
                      flowMachineName: pullWithFlowMachineName,
                      entityTypeMachineName: item.entityTypeVersion.machineName,
                      entityTypeNamespaceMachineName: item.entityTypeVersion.namespaceMachineName,
                      ...(forcePull
                        ? {}
                        : {
                            manually: true,
                          }),
                      remoteUuid: item.entity.remoteUuid,
                      remoteUniqueId: item.entity.remoteUniqueId,
                    });

                    const stateUpdate = {
                      pullingEntity: undefined,
                      syndication,
                      pullTriggered: true,
                    };

                    await this.load(stateUpdate);
                  } catch (e) {
                    this.setState({
                      pullingEntity: undefined,
                    });

                    if ((e as Error).message) toast((e as Error).message, { type: 'error' });
                  }
                })();

                return false;
              }}
            >
              {pullingEntity && !syndication ? <FontAwesomeIcon icon={faSpinner} spin /> : undefined}
              {deleted ? (
                forcePull ? (
                  'Force restore'
                ) : (
                  'Restore'
                )
              ) : (
                <>
                  {syndication ? (
                    <SyndicationStatusIcon
                      status={syndication.status}
                      errorType={syndication.operations?.[0]?.errors?.[0]?.type}
                      size="sm"
                      background={variant === 'primary' ? 'primary' : undefined}
                    />
                  ) : pullingEntity ? null : (
                    <FontAwesomeIcon icon={ICON_PULL} className="me-2" />
                  )}{' '}
                  {forcePull ? 'Force pull' : isClonableInManualContext ? cloneButtonLabel : 'Pull'}
                </>
              )}
            </Button>

            <Dropdown.Toggle split variant={variant} id="dropdown-split-basic" className="border-start bg-none" />

            {menuItems}
          </Dropdown>
        </>,
      ];
    } else {
      actions = [
        <>
          <Dropdown align="end" as={ButtonGroup} className="rounded bg-white-selectable shadow-sm" onClick={(e) => e.stopPropagation()}>
            {canViewSource ? viewSource(true, !actionDropDown) : showUpdatesButton}

            <Dropdown.Toggle split variant="light" id="dropdown-split-basic" className="border-start bg-none color-dark" />

            {menuItems}
          </Dropdown>
        </>,
      ];
    }

    const nuggets = (
      <div className={`${viewMode === 'list' ? '' : 'ps-3 '} pt-3 fs-7 mb-3 nuggets`}>
        <Nugget icon={ICON_PULL}>
          <SiteName
            id={item.sourceUsage.site.getId()}
            link={sourceDeleted ? undefined : item.sourceUsage.viewUrl}
            linkIconClassName="ms-1"
            className="text-muted"
            inline
          />
        </Nugget>
        <HasMultiplePools>
          {item.entity.pools?.length ? <PoolSummaryIcon poolReferences={item.entity.pools} wide className="text-muted" nugget /> : <></>}
        </HasMultiplePools>
        <Nugget
          icon={
            getEntityTypeIcon(item.entity.appType, item.entityTypeVersion.namespaceMachineName, item.entityTypeVersion.machineName).icon
          }
        >
          {item.entityTypeVersion.name}
        </Nugget>
        <Nugget icon={faClock}>
          <FormatDate withTime>{item.entity.createdAt}</FormatDate>
        </Nugget>
      </div>
    );

    const headingSharedComponents = (
      <>
        <div className="preview-name inline-block flex-shrink-1 align-self-center text-truncate" title={item.entity.name}>
          {pullWithFlowMachineName ? (
            deleted ? (
              <FontAwesomeIcon icon={faTrashCan} className="text-danger me-2 fs-4" />
            ) : viewUrl ? (
              <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2 fs-4" />
            ) : undefined
          ) : undefined}
          {item.entity.name || <em>Unnamed</em>}{' '}
        </div>
        {(!pullWithFlowMachineName || !deleted) &&
          item.sourceUsage.createdAt.isAfter(moment().subtract(1, 'week')) &&
          item.sourceUsage.status === EntityRemoteStatus.Exists && (
            <div className="flex-grow-0 align-self-center flex-shrink-0 ms-3 fs-4 me-3" style={{ marginLeft: '1rem !important' }}>
              <Badge bg="danger">New</Badge>
            </div>
          )}
      </>
    );

    const actionComponents = (
      <div>
        <div className="text-end pt-2 pb-3 pe-3">
          {actions.map((c, i) => (
            <React.Fragment key={i}>{c}</React.Fragment>
          ))}
        </div>
      </div>
    );

    const cloneSummary = this.state.cloneSummary;
    const cloneSummaryEntityTypes = this.state.cloneSummaryEntityTypes;
    const cloneSummaryComponent =
      !!cloneSummary?.total && cloneSummary.total > 0 ? (
        <div className="ps-3 pe-3 pb-3 pt-4 text-muted">
          <span>
            <FontAwesomeIcon icon={faClone} className="me-2" />
            {cloneSummary.total <= 3 ? (
              <>
                Cloned as{' '}
                {(cloneSummary.items || []).slice(0, 3).map((c, idx) => (
                  <React.Fragment key={c.id}>
                    {idx > 0 ? ', ' : null}
                    {(() => {
                      const isDeleted = c.status !== EntityRemoteStatus.Exists || !!(c as any).deletedAt;
                      const sharedEntityId = c.targetRemoteUuid || c.targetRemoteUniqueId;
                      const entityTypeId = c.entityType?.getId?.();
                      const entityType = entityTypeId ? cloneSummaryEntityTypes?.[String(entityTypeId)] : undefined;

                      const label = (
                        <SiteEntityName
                          entityType={entityType}
                          sharedEntityId={sharedEntityId}
                          pulledAt={c.pulledAt}
                          isDeleted={isDeleted}
                          skipResolve={isDeleted || !entityType}
                        />
                      );

                      return c.viewUrl && !isDeleted ? (
                        <a href={c.viewUrl} target="_blank" rel="noreferrer" className="text-decoration-none">
                          {label}
                        </a>
                      ) : (
                        <span>{label}</span>
                      );
                    })()}
                  </React.Fragment>
                ))}
              </>
            ) : (
              <>Has been cloned {cloneSummary.total} times.</>
            )}
          </span>{' '}
          {cloneSummary.total > 3 ? (
            <Button
              variant="link"
              className="p-0"
              onClick={() => {
                this.setState({ showCloneHistoryModal: true });
                modalOpen();
              }}
            >
              View history
            </Button>
          ) : (
            <></>
          )}
        </div>
      ) : null;

    return (
      <>
        {viewUrl ? <a id={autoOpenLinkId} href={viewUrl} target="_blank" rel="noreferrer" style={{ display: 'none' }} /> : null}

        {viewMode === 'list' && (
          <Accordion defaultActiveKey="" className="mb-3 embed-pd">
            <Accordion.Item eventKey={item.entity.id}>
              {/* The row supplies its own chevron, so suppress the library's. */}
              <Accordion.Header showChevron={false} className="!p-0">
                <div key={item.entity.id} className="preview-item d-flex align-items-center w-100">
                  <div className="flex-grow-1 d-flex flex-column align-items-start ps-3 pt-2 overflow-hidden">
                    <h2 className="mb-0 d-inline-flex justify-content-start w-100">{headingSharedComponents}</h2>
                    {nuggets}
                  </div>

                  <div className="ms-auto d-flex f-flex flex-shrink-0 ps-2 align-items-center">
                    <div className="f-flex flex-shrink-0 flex-grow-0 me-4">
                      {actions.map((c, i) => (
                        <React.Fragment key={i}>{c}</React.Fragment>
                      ))}
                    </div>
                    <FontAwesomeIcon icon={faChevronDown} className="flex-shrink-0 flex-grow-0 me-4 fs-4" />
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body className="!p-0 bg-transparent">
                <ItemPreview item={item} addTextFilter={this.props.addTextFilter} imagePreviewJwt={imagePreviewJwt} />
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        )}

        {viewMode === 'preview' && (
          <div key={item.entity.id} className="preview-item rounded shadow-sm my-4 bg-white">
            <h2 className="mb-0 d-inline-flex justify-content-start ps-3 pt-2 w-100">{headingSharedComponents}</h2>
            {nuggets}
            <ItemPreview item={item} addTextFilter={this.props.addTextFilter} imagePreviewJwt={imagePreviewJwt} />
            <LeftRightContainer left={cloneSummaryComponent} right={actionComponents} />
          </div>
        )}

        {serialize && (
          <WithSite entityId={item.sourceUsage.site.getId()}>
            {(site) => (
              <WithMostRecentSyndications
                siteUuid={site?.uuid}
                type={{
                  namespaceMachineName: item.entityTypeVersion.namespaceMachineName,
                  machineName: item.entityTypeVersion.machineName,
                }}
                entity={item.entity}
              >
                {(recent) =>
                  recent ? (
                    <WithRemoteRevisionEntity entityId={item.entity.id}>
                      {(entity) =>
                        entity && (
                          <Serialize
                            item={entity}
                            serializeMode={serializeMode}
                            onHide={() => {
                              this.setState({ serialize: false });
                              modalClose();
                            }}
                            revision={recent}
                            selectedComparison={selectedComparison}
                            selectedComparisonId={selectedComparisonId}
                            handleSerializeModeUpdate={(v: SerializedModeType) => {
                              this.setState({ serializeMode: v });
                            }}
                            handleSelectedComparisonUpdate={(selectedComparison?: number, selectedComparisonId?: InternalId) => {
                              this.setState({
                                selectedComparison,
                                selectedComparisonId,
                              });
                            }}
                          />
                        )
                      }
                    </WithRemoteRevisionEntity>
                  ) : (
                    <></>
                  )
                }
              </WithMostRecentSyndications>
            )}
          </WithSite>
        )}

        {showUpdate && (
          <EmbeddedModal scrollable size="xl" show onHide={() => this.setState({ showUpdate: false })}>
            <Modal.Header closeButton>
              <Modal.Title className="w-100" title={item.entity.name}>
                <span className="text-truncate mw-100">{item.entity.name || <em>Unnamed</em>}</span>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <EntityStatusWithParams
                embedded
                params={{
                  configurationAccess: true,
                  namespaceMachineName: item.entityTypeVersion.namespaceMachineName,
                  machineName: item.entityTypeVersion.machineName,
                  remoteUniqueId: item.entity.remoteUniqueId,
                  remoteUuid: item.entity.remoteUuid,
                }}
              />
            </Modal.Body>
          </EmbeddedModal>
        )}

        {showPullModeModal && manualPullFlowOptions && (
          <EmbeddedModal
            centered
            scrollable
            size="lg"
            show
            onHide={() => {
              this.setState({ showPullModeModal: false });
              modalClose();
            }}
          >
            <Modal.Header closeButton>
              <Modal.Title className="w-100" title={item.entity.name}>
                <span className="text-truncate mw-100">{item.entity.name || <em>Unnamed</em>}</span>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="text-muted mb-3">
                Select what you want to do with this <em>{item.entityTypeVersion.name}</em>.
              </div>

              {(() => {
                const cloneCount = manualPullFlowOptions.filter((o) => !!o.clone).length;
                const nonCloneCount = manualPullFlowOptions.length - cloneCount;
                const showFlowNameForClone = cloneCount > 1;
                const showFlowNameForNonClone = nonCloneCount > 1;

                return (
                  <div className="d-grid" style={{ gap: '12px' }}>
                    {manualPullFlowOptions.map((o) => {
                      const selected = selectedManualFlow === o.machineName;
                      const isClone = !!o.clone;

                      const existsHere = item.localUsage?.status === EntityRemoteStatus.Exists;
                      const disableBecauseAlreadySynced = existsHere && !isClone;

                      const title = isClone
                        ? showFlowNameForClone
                          ? o.name
                          : 'Clone to this site'
                        : showFlowNameForNonClone
                          ? o.name
                          : 'Keep in sync';

                      const description = isClone
                        ? 'Clone the content to this site to edit freely. You can clone the same content multiple times.'
                        : 'Automatically stay up to date with any changes made on the source site.';

                      const icon = isClone ? faClone : faArrowsRotate;

                      return (
                        <div key={o.machineName}>
                          <Button
                            variant={selected ? 'primary' : 'light'}
                            className="text-start rounded p-3 w-100"
                            disabled={disableBecauseAlreadySynced}
                            onClick={() => {
                              if (!disableBecauseAlreadySynced) {
                                this.setState({ selectedManualFlow: o.machineName });
                              }
                            }}
                          >
                            <div className="d-flex align-items-start">
                              <div className="me-3 mt-1">
                                <FontAwesomeIcon icon={icon} />
                              </div>
                              <div className="flex-grow-1">
                                <div className={selected ? 'fw-bold text-white' : 'fw-bold'}>{title}</div>
                                <div className={selected ? 'fw-normal text-white' : 'fw-normal text-muted'}>{description}</div>
                              </div>
                            </div>
                          </Button>

                          {disableBecauseAlreadySynced && item.localUsage?.viewUrl ? (
                            <div
                              style={{
                                position: 'relative',
                                top: '-0.5em',
                                left: '3em',
                                width: '200px',
                              }}
                            >
                              Already available{' '}
                              <em>
                                <a
                                  href={item.localUsage.viewUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-decoration-none"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  here
                                </a>
                              </em>
                              .
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </Modal.Body>

            <Modal.Footer className="d-flex justify-content-between align-items-center">
              <Form.Check
                type="checkbox"
                id={`pd-open-when-done-${item.entity.id}`}
                label="Open when done"
                checked={openWhenDone}
                onChange={(e) => {
                  const value = (e.target as HTMLInputElement).checked;
                  localStorage.setItem(OPEN_WHEN_DONE_STORAGE_KEY, value ? 'true' : 'false');
                  this.setState({ openWhenDone: value });
                }}
              />
              <div>
                <Button
                  variant="light"
                  className="me-2"
                  onClick={() => {
                    this.setState({ showPullModeModal: false });
                    modalClose();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  disabled={!selectedManualFlow}
                  onClick={() => {
                    const flowMachineName = selectedManualFlow || manualPullFlowOptions[0].machineName;

                    this.setState({ showPullModeModal: false, pullingEntity: true, syndication: undefined, openWhenDoneArmed: true });
                    modalClose();

                    (async () => {
                      try {
                        const syndication = await this.api.syndication.syndications.pull({
                          flowMachineName,
                          entityTypeMachineName: item.entityTypeVersion.machineName,
                          entityTypeNamespaceMachineName: item.entityTypeVersion.namespaceMachineName,
                          manually: true,
                          remoteUuid: item.entity.remoteUuid,
                          remoteUniqueId: item.entity.remoteUniqueId,
                        });

                        await this.load({ pullingEntity: undefined, pullTriggered: true, syndication });
                      } catch (e) {
                        this.setState({ pullingEntity: undefined });
                        if ((e as Error).message) toast((e as Error).message, { type: 'error' });
                      }
                    })();
                  }}
                >
                  Continue
                </Button>
              </div>
            </Modal.Footer>
          </EmbeddedModal>
        )}

        {showCloneHistoryModal && this.state.cloneSummary?.total ? (
          <Modal
            show
            size="lg"
            centered
            onHide={() => {
              this.setState({ showCloneHistoryModal: false });
              modalClose();
            }}
          >
            <Modal.Header closeButton>
              <Modal.Title>Clones</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <RemoteEntityCloneList
                pageSize={5}
                filters={{
                  sourceRemoteUuid: item.entity.remoteUuid,
                }}
              />
            </Modal.Body>
          </Modal>
        ) : null}
      </>
    );
  }
}
